import React, { useState, useCallback } from 'react';

/**
 * BuilderEmbedded — stripped-down build tools for Godot WebView.
 * No header, footer, avatar, chat, navigation.
 * Just the build toolbar + properties panel.
 * Communicates with Godot via GodotBridge.postMessage().
 */

const SHAPES = [
  { name: 'Box', shape: 'box', icon: '◼' },
  { name: 'Sphere', shape: 'sphere', icon: '●' },
  { name: 'Cylinder', shape: 'cylinder', icon: '⬮' },
  { name: 'Cone', shape: 'cone', icon: '▲' },
  { name: 'Torus', shape: 'torus', icon: '◎' },
  { name: 'Plane', shape: 'plane', icon: '▬' },
];

const COLORS = [
  '#ef4444', '#f97316', '#eab308', '#84cc16',
  '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6',
  '#6366f1', '#8b5cf6', '#ec4899', '#ffffff',
  '#d4d4d4', '#888888', '#444444', '#00c2b3',
];

const TEXTURES = [
  { name: 'Grass', key: 'grass' },
  { name: 'Wood', key: 'wood1' },
  { name: 'Brick', key: 'brick' },
  { name: 'Marble', key: 'marble' },
  { name: 'Tile', key: 'tile1' },
  { name: 'Cobble', key: 'cobblestone' },
  { name: 'Sand', key: 'sand' },
  { name: 'Dirt', key: 'dirt' },
];

// Send command to Godot via JavaScript bridge
function sendToGodot(action: string, data: Record<string, any> = {}) {
  const message = JSON.stringify({ action, ...data });
  if ((window as any).GodotBridge) {
    (window as any).GodotBridge.postMessage(message);
  } else {
    console.log('[Embedded] No GodotBridge:', message);
  }
}

type Tab = 'shapes' | 'transform' | 'colors' | 'textures' | 'actions';

const BuilderEmbedded: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('shapes');

  const handlePlaceShape = useCallback((shape: string) => {
    sendToGodot('place_shape', { shape });
  }, []);

  const handleApplyColor = useCallback((color: string) => {
    sendToGodot('apply_color', { color });
  }, []);

  const handleApplyTexture = useCallback((key: string) => {
    sendToGodot('apply_texture', { texturePreset: key });
  }, []);

  const handleAction = useCallback((action: string) => {
    sendToGodot(action);
  }, []);

  return (
    <div style={styles.container}>
      {/* Tab bar */}
      <div style={styles.tabBar}>
        {(['shapes', 'transform', 'colors', 'textures', 'actions'] as Tab[]).map((tab) => (
          <button
            key={tab}
            style={{
              ...styles.tab,
              ...(activeTab === tab ? styles.tabActive : {}),
            }}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
        <button style={styles.closeBtn} onClick={() => sendToGodot('close_menu')}>
          ✕
        </button>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {activeTab === 'shapes' && (
          <div style={styles.grid3}>
            {SHAPES.map((s) => (
              <button
                key={s.shape}
                style={styles.shapeCard}
                onClick={() => handlePlaceShape(s.shape)}
              >
                <span style={styles.shapeIcon}>{s.icon}</span>
                <span style={styles.shapeLabel}>{s.name}</span>
              </button>
            ))}
          </div>
        )}

        {activeTab === 'transform' && (
          <div style={styles.transformPanel}>
            <p style={styles.hint}>Select an object first</p>

            <div style={styles.transformSection}>
              <span style={styles.transformLabel}>Scale</span>
              <div style={styles.transformRow}>
                <button style={styles.transformBtn} onClick={() => sendToGodot('scale', { axis: 'all', delta: -0.2 })}>−</button>
                <button style={styles.transformBtnWide} onClick={() => sendToGodot('scale', { axis: 'all', delta: -0.05 })}>Smaller</button>
                <button style={styles.transformBtnWide} onClick={() => sendToGodot('scale', { axis: 'all', delta: 0.05 })}>Bigger</button>
                <button style={styles.transformBtn} onClick={() => sendToGodot('scale', { axis: 'all', delta: 0.2 })}>+</button>
              </div>
              <div style={styles.transformRow}>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('scale', { axis: 'x', delta: 0.1 })}>X+</button>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('scale', { axis: 'x', delta: -0.1 })}>X−</button>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('scale', { axis: 'y', delta: 0.1 })}>Y+</button>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('scale', { axis: 'y', delta: -0.1 })}>Y−</button>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('scale', { axis: 'z', delta: 0.1 })}>Z+</button>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('scale', { axis: 'z', delta: -0.1 })}>Z−</button>
              </div>
            </div>

            <div style={styles.transformSection}>
              <span style={styles.transformLabel}>Rotate</span>
              <div style={styles.transformRow}>
                <button style={styles.transformBtnWide} onClick={() => sendToGodot('rotate', { axis: 'y', degrees: -15 })}>← 15°</button>
                <button style={styles.transformBtnWide} onClick={() => sendToGodot('rotate', { axis: 'y', degrees: -45 })}>← 45°</button>
                <button style={styles.transformBtnWide} onClick={() => sendToGodot('rotate', { axis: 'y', degrees: 45 })}>45° →</button>
                <button style={styles.transformBtnWide} onClick={() => sendToGodot('rotate', { axis: 'y', degrees: 15 })}>15° →</button>
              </div>
              <div style={styles.transformRow}>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('rotate', { axis: 'x', degrees: 15 })}>Tilt ↑</button>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('rotate', { axis: 'x', degrees: -15 })}>Tilt ↓</button>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('rotate', { axis: 'z', degrees: 15 })}>Roll ↺</button>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('rotate', { axis: 'z', degrees: -15 })}>Roll ↻</button>
              </div>
            </div>

            <div style={styles.transformSection}>
              <span style={styles.transformLabel}>Position</span>
              <div style={styles.transformRow}>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('move', { axis: 'y', delta: 0.25 })}>Up</button>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('move', { axis: 'y', delta: -0.25 })}>Down</button>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('move', { axis: 'x', delta: 0.25 })}>Right</button>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('move', { axis: 'x', delta: -0.25 })}>Left</button>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('move', { axis: 'z', delta: 0.25 })}>Fwd</button>
                <button style={styles.transformBtnAxis} onClick={() => sendToGodot('move', { axis: 'z', delta: -0.25 })}>Back</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'colors' && (
          <>
            <p style={styles.hint}>Select an object first, then tap a color</p>
            <div style={styles.colorGrid}>
              {COLORS.map((c) => (
                <button
                  key={c}
                  style={{ ...styles.colorSwatch, backgroundColor: c }}
                  onClick={() => handleApplyColor(c)}
                />
              ))}
            </div>
          </>
        )}

        {activeTab === 'textures' && (
          <>
            <div style={styles.grid4}>
              {TEXTURES.map((t) => (
                <button
                  key={t.key}
                  style={styles.textureBtn}
                  onClick={() => handleApplyTexture(t.key)}
                >
                  {t.name}
                </button>
              ))}
            </div>
            <button style={styles.pbrBtn} onClick={() => sendToGodot('browse_pbr')}>
              Browse PBR Textures
            </button>
          </>
        )}

        {activeTab === 'actions' && (
          <div style={styles.actionList}>
            <button style={{ ...styles.actionBtn, ...styles.actionSelect }} onClick={() => handleAction('select_mode')}>
              Select Mode
            </button>
            <button style={{ ...styles.actionBtn, ...styles.actionDuplicate }} onClick={() => handleAction('duplicate')}>
              Duplicate
            </button>
            <button style={{ ...styles.actionBtn, ...styles.actionDelete }} onClick={() => handleAction('delete')}>
              Delete
            </button>
            <div style={styles.divider} />
            <button style={{ ...styles.actionBtn, ...styles.actionHome }} onClick={() => handleAction('go_home')}>
              Return Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    height: '100vh',
    background: '#080e18',
    color: '#eef0f5',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  tabBar: {
    display: 'flex',
    gap: 4,
    padding: '8px 8px 0',
    borderBottom: '1px solid rgba(0,194,179,0.15)',
    background: '#0a1020',
  },
  tab: {
    flex: 1,
    padding: '10px 6px',
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: 'rgba(255,255,255,0.5)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: '6px 6px 0 0',
  },
  tabActive: {
    color: '#00c2b3',
    borderBottomColor: '#00c2b3',
    background: 'rgba(0,194,179,0.08)',
  },
  closeBtn: {
    width: 36,
    background: 'rgba(248,113,113,0.15)',
    border: 'none',
    color: '#f87171',
    fontSize: 16,
    cursor: 'pointer',
    borderRadius: '6px 6px 0 0',
  },
  content: {
    flex: 1,
    padding: 12,
    overflowY: 'auto',
  },
  grid3: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
  },
  grid4: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 6,
  },
  shapeCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 6,
    padding: '16px 8px',
    background: '#111827',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    color: '#eef0f5',
    cursor: 'pointer',
    fontSize: 13,
    transition: 'all 0.15s',
  },
  shapeIcon: {
    fontSize: 28,
    color: '#00c2b3',
  },
  shapeLabel: {
    fontSize: 13,
    opacity: 0.8,
  },
  hint: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.35)',
    textAlign: 'center' as const,
    marginBottom: 10,
  },
  colorGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 8,
  },
  colorSwatch: {
    width: '100%',
    aspectRatio: '1',
    border: '2px solid rgba(255,255,255,0.1)',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  textureBtn: {
    padding: '10px 4px',
    background: '#111827',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 8,
    color: '#eef0f5',
    fontSize: 12,
    cursor: 'pointer',
  },
  pbrBtn: {
    width: '100%',
    marginTop: 10,
    padding: '12px 16px',
    background: 'rgba(0,119,182,0.2)',
    border: '1px solid rgba(0,119,182,0.3)',
    borderRadius: 8,
    color: '#0077b6',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  actionList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  actionBtn: {
    padding: '14px 16px',
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left' as const,
  },
  actionSelect: {
    background: 'rgba(0,194,179,0.15)',
    color: '#00c2b3',
  },
  actionDuplicate: {
    background: 'rgba(34,197,94,0.15)',
    color: '#22c55e',
  },
  actionDelete: {
    background: 'rgba(248,113,113,0.15)',
    color: '#f87171',
  },
  actionHome: {
    background: 'rgba(0,119,182,0.15)',
    color: '#0077b6',
  },
  divider: {
    height: 1,
    background: 'rgba(255,255,255,0.08)',
    margin: '4px 0',
  },
  transformPanel: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 14,
  },
  transformSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 6,
  },
  transformLabel: {
    fontSize: 12,
    color: '#00c2b3',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: 1,
  },
  transformRow: {
    display: 'flex',
    gap: 4,
  },
  transformBtn: {
    width: 44,
    height: 40,
    background: '#111827',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    color: '#eef0f5',
    fontSize: 18,
    fontWeight: 700,
    cursor: 'pointer',
  },
  transformBtnWide: {
    flex: 1,
    height: 40,
    background: '#111827',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    color: '#eef0f5',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  transformBtnAxis: {
    flex: 1,
    height: 36,
    background: 'rgba(0,194,179,0.08)',
    border: '1px solid rgba(0,194,179,0.15)',
    borderRadius: 6,
    color: '#00c2b3',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default BuilderEmbedded;
